import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { IoAddOutline, IoPencil, IoTrash } from "react-icons/io5";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useSearchParams } from "react-router";
import axios from "axios"; // ✅ Import axios
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineDelete } from "react-icons/md";
import api from "../../utils/api";

function Departments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
   const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    departmentName: "",
    description: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(8);

  const baseUrl = process.env.REACT_APP_API_URL;

  // Fetch Departments with pagination + search
  const fetchDepartments = async (page = 1, size = pageSize) => {
    setLoading(true);
    try {

      const searchTerm = searchParams.get("search") || "";

      const response = await api.get(`/api/departments`, {
        params: {
          page: page - 1,
          size,
          search: searchTerm.trim(),
        },
      });

      const data = response.data;
      setDepartments(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage((data.currentPage ?? page - 1) + 1);
    } catch (error) {
      console.error("Error fetching departments", error);
    }
    setLoading(false);
  };

  

  useEffect(() => {
      fetchDepartments(currentPage);
    }, [currentPage]);
  
  
    useEffect(() => {
      setCurrentPage(1); // Reset to first page
      fetchDepartments(1);
    }, [searchParams]);



  // Handle form input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Add/Edit Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) {
        await api.put(`/api/departments/${editingDept.id}`, formData);
      } else {
        await api.post(`/api/departments`, formData);
      }
      setShowModal(false);
      setFormData({ departmentName: "", description: "" });
      setEditingDept(null);
      fetchDepartments(currentPage, pageSize);
    } catch (error) {
      console.error("Error saving department", error);
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this department?")) {
      try {
        await api.delete(`/api/departments/${id}`);
        fetchDepartments(currentPage, pageSize);
      } catch (error) {
        console.error("Error deleting department", error);
      }
    }
  };

  // Open Modal for Add/Edit
  const openModal = (dept = null) => {
    if (dept) {
      setEditingDept(dept);
      setFormData({
        departmentName: dept.departmentName,
        description: dept.description,
      });
    } else {
      setEditingDept(null);
      setFormData({ departmentName: "", description: "" });
    }
    setShowModal(true);
  };

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 mb-20 w-full mx-auto">
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h2 className="text-xl xs:text-2xl font-bold">Departments</h2>
        <motion.button
          onClick={() => openModal()}
          className="bg-slate-800 text-white px-4 py-2 rounded-full hover:bg-slate-700 flex items-center gap-2"
        >
          <IoAddOutline className="text-lg" />
          <span>Add Department</span>
        </motion.button>
      </motion.div>

      
      {/* Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">SL No</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Department Name</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Description</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">No. of Employees</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-6">No departments found</td>
                </tr>
              ) : (
                departments.map((dept, index) => (
                  <tr key={dept.id} className="hover:bg-slate-50 transition-all">
                    <td className="px-6 py-4">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className="px-6 py-4 font-medium">{dept.departmentName}</td>
                    <td className="px-6 py-4">{dept.description}</td>
                    <td className="px-6 py-4">{dept.employeeCount || 0}</td>
                    <td className="px-6 py-4 text-center flex justify-center gap-3">
                      <button onClick={() => openModal(dept)} className="text-blue-600 hover:text-blue-800">
                        <FaRegEdit className="text-xl" />
                      </button>
                      <button onClick={() => handleDelete(dept.id)} className="text-red-600 hover:text-red-800">
                        <MdOutlineDelete  className="text-xl" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {departments.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-[400px]">
            <h3 className="text-lg font-bold mb-4">
              {editingDept ? "Edit Department" : "Add Department"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="departmentName"
                placeholder="Department Name"
                value={formData.departmentName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              <textarea
                name="description"
                placeholder="Description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
                >
                  {editingDept ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Departments;
